// Shared mission-steps content: used by the full Mission page and by the
// ShopMap "start mission" popup so both read as the exact same list.

function PinIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M12 22s7-6.4 7-12a7 7 0 0 0-14 0c0 5.6 7 12 7 12Z" stroke="#9051c3" strokeWidth="1.8" strokeLinejoin="round" />
      <circle cx="12" cy="10" r="2.4" stroke="#9051c3" strokeWidth="1.8" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M4 8.5A1.5 1.5 0 0 1 5.5 7H8l1-2h6l1 2h2.5A1.5 1.5 0 0 1 20 8.5v9A1.5 1.5 0 0 1 18.5 19h-13A1.5 1.5 0 0 1 4 17.5v-9Z" stroke="#9051c3" strokeWidth="1.8" strokeLinejoin="round" />
      <circle cx="12" cy="13" r="3.2" stroke="#9051c3" strokeWidth="1.8" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="20" height="22" viewBox="0 0 24 24" fill="none">
      <rect x="6" y="2" width="12" height="20" rx="2.5" stroke="#9051c3" strokeWidth="1.8" />
      <circle cx="12" cy="18.3" r="1" fill="#9051c3" />
      <path d="M15 6.5l3-1.5M15 9l3.6-.6" stroke="#9051c3" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function GiftIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <rect x="4" y="10" width="16" height="10" rx="1.5" stroke="#9051c3" strokeWidth="1.8" />
      <rect x="4" y="7" width="16" height="4" rx="1.2" stroke="#9051c3" strokeWidth="1.8" />
      <path d="M12 7v13M12 7c-1.6-3-6-3-6 0s4.4 0 6 0Zm0 0c1.6-3 6-3 6 0s-4.4 0-6 0Z" stroke="#9051c3" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

// icon_key (from Laravel mission_steps) -> existing icon component. Keys are
// matched case-insensitively. The database has no seeded mission_steps yet,
// so these cover the icon_key values that plausibly correspond to today's
// 4 hardcoded steps (discover/selfie/post/reward); anything null or
// unrecognized falls back to ICON_BY_STEP_NUMBER below instead of guessing.
const ICON_KEY_MAP = {
  pin: PinIcon,
  location: PinIcon,
  discover: PinIcon,
  camera: CameraIcon,
  selfie: CameraIcon,
  phone: PhoneIcon,
  post: PhoneIcon,
  share: PhoneIcon,
  gift: GiftIcon,
  reward: GiftIcon,
  points: GiftIcon,
};

// Positional fallback used only when icon_key is missing/unrecognized. This
// is a UI-only visual choice — it never fabricates mission-step data.
const ICON_BY_STEP_NUMBER = {
  1: PinIcon,
  2: CameraIcon,
  3: PhoneIcon,
  4: GiftIcon,
};

function resolveStepIcon(iconKey, stepNumber) {
  const key = typeof iconKey === 'string' ? iconKey.trim().toLowerCase() : null;
  const IconComponent = (key && ICON_KEY_MAP[key]) || ICON_BY_STEP_NUMBER[stepNumber] || PinIcon;
  return <IconComponent />;
}

// Normalizes either step shape MissionStepsList can receive:
//  - legacy hardcoded shape: { n, color, icon (JSX), text (JSX/string) }
//  - dynamic Laravel-backed shape (missionStepAdapter output): { id,
//    stepNumber, title, subtitle, iconKey, color, ... }
function normalizeStep(step) {
  const isLegacyShape = step.icon !== undefined && step.text !== undefined && step.n !== undefined;

  if (isLegacyShape) {
    return {
      key: step.n,
      number: step.n,
      color: step.color,
      icon: step.icon,
      content: step.text,
    };
  }

  const number = step.stepNumber ?? step.n;
  const color = '#000000';

  return {
    key: step.id ?? number,
    number,
    color,
    icon: resolveStepIcon(step.iconKey, number),
    content: (
      <>
        <span style={{ color }}>{step.title}</span>
        {step.subtitle && (
          <>
            <br />
            <span style={{ fontWeight: 500, opacity: 0.7, fontSize: '0.85em' }}>{step.subtitle}</span>
          </>
        )}
      </>
    ),
  };
}

export const MISSION_STEPS = [
  {
    n: 1,
    color: '#9051c3',
    icon: <PinIcon />,
    text: (
      <>
        Discover the <span style={{ color: '#000000' }}>cafe</span>.
      </>
    ),
  },
  {
    n: 2,
    color: '#9051c3',
    icon: <CameraIcon />,
    text: (
      <>
        Take a <span style={{ color: '#000000' }}>selfie</span> with Zini.
      </>
    ),
  },
  {
    n: 3,
    color: '#9051c3',
    icon: <PhoneIcon />,
    text: (
      <>
        Post it with <span style={{ color: '#000000' }}>#FoundByZini</span> and tag the cafe + DealZta.
      </>
    ),
  },
  {
    n: 4,
    color: '#9051c3',
    icon: <GiftIcon />,
    text: (
      <>
        Collect the points and unlock your <span style={{ color: '#000000' }}>surprise deal!</span>
      </>
    ),
  },
];

export default function MissionStepsList({ steps = MISSION_STEPS }) {
  const normalizedSteps = steps.map(normalizeStep);

  return (
    <ol className="mission-list">
      {normalizedSteps.map((step) => (
        <li className="mission-row" key={step.key}>
          <span
            className="mission-badge"
            style={{ background: '#ffffff', color: '#9051c3', border: '2px solid #9051c3' }}
          >
            {step.number}
          </span>
          <span className="mission-row-text">{step.content}</span>
          <span className="mission-row-icon">{step.icon}</span>
        </li>
      ))}
    </ol>
  );
}
